import { motion, AnimatePresence } from "framer-motion";
import { Scan } from "lucide-react";
import { useEffect, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  FaLeaf,
  FaSeedling,
  FaStar,
  FaShoppingCart,
  FaPlus,
  FaMinus,
  FaComment,
  FaSearchPlus,
  FaTimes,
} from "react-icons/fa";
import { FaWheatAwn } from "react-icons/fa6";
import OptimizedImage from "~/components/shared/imageHandler/OptimizedImage";
import { useBranch } from "~/contexts/BranchContext";
import { useShoppingCart } from "~/contexts/ShoppingCartContext";
import type { MenuItem } from "~/types/new_types";
import {
  getItemDescription,
  getItemName,
  itemIs,
} from "~/utils/menuItemHelper";
import { useBodyScrollLock } from "~/hooks/useBodyScrollLock";
type MenuItemGridCardProps = {
  item: MenuItem;
  onClick?: () => void;
};

const MenuItemGridCard: React.FC<MenuItemGridCardProps> = ({
  item,
  onClick,
}) => {
  const { t, i18n } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const { setTempQuantity, getTempQuantity } = useShoppingCart();

  const { branch, theme, getConvertedPrice } = useBranch();
  const isRTL = i18n.language === "ar";

  // Prevent body scroll when image modal is open (fixes mobile flickering)
  useBodyScrollLock(showImageModal);

  // Convert prices based on selected currency
  const displayPrice = getConvertedPrice(item.price);
  const displayDiscountedPrice = item.discounted_price ? getConvertedPrice(item.discounted_price) : null;

  const currentQuantity = getTempQuantity(item.id);

  // Normalize 0-1 backend rating to a 0-5 display scale
  const rawRating = Number(item.item_rate ?? 0);
  const normalizedRating = Number.isFinite(rawRating)
    ? Math.max(0, Math.min(5, rawRating * 5))
    : 0;
  const roundedRating = Math.round(normalizedRating * 2) / 2;
  const hasRating = roundedRating > 0;
  const formattedRating = hasRating
    ? isRTL
      ? roundedRating.toLocaleString("ar-EG", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
      : roundedRating.toFixed(1)
    : null;

  const handleFirstAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.is_available) return;
    setTempQuantity(item.id, 1, [], false);
  };

  const handleQuantityIncrease = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.is_available) return;
    const newQuantity = currentQuantity + 1;
    setTempQuantity(item.id, newQuantity, [], false);
  };

  const handleQuantityDecrease = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.is_available) return;
    const newQuantity = Math.max(0, currentQuantity - 1);
    setTempQuantity(item.id, newQuantity, [], false);
  };

  return (
    <>
      <motion.div
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        whileHover={{
          y: -8,
          boxShadow: `0 20px 25px -5px ${theme?.primaryColor}30`,
        }}
        onClick={onClick}
        className="relative rounded-xl overflow-hidden cursor-pointer h-full flex flex-col"
        style={{
          backgroundColor: theme?.cardColor,
          borderRadius: theme?.borderRadius,
          border:
            theme?.cardStyle == "bordered"
              ? `2px solid ${theme?.borderColor}`
              : "none",
          boxShadow: isHovered
            ? `0 12px 24px ${theme?.primaryColor}30`
            : theme?.cardStyle == "shadow"
              ? `0 6px 12px ${theme?.primaryColor}30`
              : "none",
        }}
      >
        {/* Image Section */}
        <div className="relative aspect-[4/3] overflow-hidden">


          <OptimizedImage
            src={item.image_url ?? branch?.default_item_image}
            alt={item.name}
            width={400}
            quality={75}
            className="w-full h-full object-cover pointer-events-none select-none"
            animate={{ scale: isHovered ? 1.05 : 1 }}
            transition={{ duration: 0.4 }}
          />

          {/* Badges & Tags */}
          <div className="absolute top-2 left-2 md:top-3 md:left-3 flex flex-wrap gap-1 max-w-[50%]">
            {item.is_featured && (
              <div
                className="px-2 py-1 bg-yellow-50 text-yellow-700 text-xs font-semibold flex items-center gap-1"
                style={{
                  borderRadius: theme?.borderRadius,
                }}
              >
                <FaStar className="text-yellow-500" />
                <span>{t("menu.popular")}</span>
              </div>
            )}
          </div>

          {/* Zoom Button */}
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              setShowImageModal(true);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="absolute cursor-pointer bottom-2 right-2 md:bottom-3 md:right-3 z-1 w-8 h-8 flex items-center justify-center backdrop-blur-sm transition-all hover:scale-110"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            style={{
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: theme?.primaryColor,
              borderRadius: '6px',
            }}
            title={t('menu.zoomImage', { defaultValue: 'Zoom image' })}
          >
            <Scan size={14} />
          </motion.button>
          {/* Unavailable overlay */}
          {!item.is_available && (
            <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
              {/* <span className="px-3 py-1 rounded-lg text-red-500 text-2xl text-shadow-2xs text-shadow-amber-100 font-bold -rotate-30">
									{t('menuItem.unavailable')}
								</span> */}
              <motion.img
                loading="lazy"
                src={`/images/not-availbale-stampel-${i18n.language}.png`}
                className="h-2/3 -rotate-10 pointer-events-none select-none"
                animate={{ scale: isHovered ? 1.05 : 1 }}
                transition={{ duration: 0.4 }}
              />
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="relative p-2 md:p-4 flex flex-col justify-between flex-grow">
          {!item.is_available && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center"></div>
          )}

          {/* Title and description */}
          <div className="mb-3">
            <h3
              className={`${theme?.itemFontSize} ${theme?.boldTitles ? "font-bold" : ""
                } mb-1`}
              style={{ color: theme?.textColor }}
              dir={i18n.language === "ar" ? "rtl" : "ltr"}
            >
              {getItemName(item, i18n.language)}
              {/* {item.is_featured && !item.image_url && (
									<span className="ms-2 px-2 py-0.5 text-xs font-semibold rounded-full text-white bg-yellow-500">
										{t('menu.popular')}
									</span>
								)} */}
            </h3>

            {/* Star Rating */}
            {hasRating && (
              <div
                className="flex items-center gap-1 mb-2"
                style={{ direction: isRTL ? "rtl" : "ltr" }}
              >
                {Array.from({ length: 5 }).map((_, index) => {
                  const fill = Math.min(
                    Math.max(roundedRating - index, 0),
                    1
                  );
                  const fillStyle: CSSProperties = {
                    width: `${fill * 100}%`,
                    height: "100%",
                    position: "absolute",
                    top: 0,
                    overflow: "hidden",
                    ...(isRTL ? { right: 0 } : { left: 0 }),
                  };

                  return (
                    <span
                      key={index}
                      className="relative inline-flex"
                      style={{ width: 12, height: 12 }}
                    >
                      <FaStar
                        size={12}
                        className="text-gray-300"
                        aria-hidden="true"
                      />
                      {fill > 0 && (
                        <span style={fillStyle} aria-hidden="true">
                          <FaStar size={12} className="text-yellow-500" />
                        </span>
                      )}
                    </span>
                  );
                })}
                {formattedRating && (
                  <span
                    className={`text-xs ${isRTL ? "mr-1" : "ml-1"}`}
                    style={{ color: `${theme?.textColor}60` }}
                  >
                    ({formattedRating})
                  </span>
                )}
              </div>
            )}

            {getItemDescription(item, i18n.language) && (
              <p
                className="text-sm line-clamp-2"
                style={{ color: `${theme?.textColor}80` }}
                dir={i18n.language === "ar" ? "rtl" : "ltr"}
              >
                {getItemDescription(item, i18n.language)}
              </p>
            )}
          </div>

          {/* Dietary icons and price with metadata */}
          <div className="flex justify-between items-end">
            <div className="flex gap-2">
              {itemIs(item, "vegetarian") && (
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: `${theme?.accentColor}20`,
                    color: theme?.accentColor,
                  }}
                  title={t("menu.vegetarian")}
                >
                  <FaLeaf size={12} />
                </div>
              )}
              {itemIs(item, "vegan") && (
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: `${theme?.accentColor}30`,
                    color: theme?.accentColor,
                  }}
                  title={t("menu.vegan")}
                >
                  <FaSeedling size={12} />
                </div>
              )}
              {itemIs(item, "gluten-free") && (
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: `${theme?.accentColor}20`,
                    color: theme?.accentColor,
                  }}
                  title={t("menu.glutenFree")}
                >
                  <FaWheatAwn size={12} />
                </div>
              )}
            </div>

            <div className="flex items-center gap-1">
              {/* Spice level indicator - show on mobile too */}
              {item.spice_level && item.spice_level !== "none" && (
                <div
                  className="flex items-center gap-0.5"
                  title={t(`spiceLevels.${item.spice_level}`)}
                >
                  {Array.from({
                    length:
                      item.spice_level === "mild"
                        ? 1
                        : item.spice_level === "medium"
                          ? 2
                          : item.spice_level === "hot"
                            ? 3
                            : 4,
                  }).map((_, i) => (
                    <span key={i} className="text-red-500 text-sm xs:text-base">
                      🌶️
                    </span>
                  ))}
                </div>
              )}

              {/* Preparation time - More prominent */}
              {item.preparation_time && (
                <div className="flex items-center xs:gap-1.5 text-sm xs:text-base text-gray-500 font-medium">
                  <span className="text-base">⏱️</span>
                  <span className="hidden xs:inline">
                    {item.preparation_time} {t("menu.minutes")}
                  </span>
                  <span className="xs:hidden">{item.preparation_time}m</span>
                </div>
              )}

              <div
                className="flex items-center gap-1 text-sm px-2 py-1 rounded font-bold"
                style={{
                  backgroundColor: `${theme?.primaryColor}15`,
                  color: theme?.primaryColor,
                  borderRadius: theme?.borderRadius,
                }}
              >
                {displayDiscountedPrice ? (
                  <div className="flex flex-col items-end">
                    <span className="text-xs line-through opacity-70">
                      {displayPrice}
                    </span>
                    <span>
                      {displayDiscountedPrice}
                    </span>
                  </div>
                ) : (
                  <span>
                    {displayPrice}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Image Zoom Modal */}
      {showImageModal && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowImageModal(false);
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              setShowImageModal(false);
            }}
          >
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                setShowImageModal(false);
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                setShowImageModal(false);
              }}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white transition-colors z-10"
              style={{
                borderRadius: '6px',
                color: theme?.primaryColor,
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaTimes size={20} />
            </motion.button>

            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative w-full flex items-center justify-center"
              style={{ maxHeight: 'calc(100vh - 2rem)' }}
              onClick={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              <img
                src={
                  item.image_url ??
                  branch?.default_item_image ??
                  "/images/no-image-available.jpg"
                }
                alt={item.name}
                className="max-w-full max-h-full object-contain rounded-lg"
                style={{ maxHeight: 'calc(100vh - 2rem)' }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/images/broken-image.jpg";
                }}
              />
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default MenuItemGridCard;
